import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { SectionCard, ToggleSwitch, TWButton, TWDataTable } from '../../../components/ui';
import { cn } from '../../../components/ui/_cn';
import { formatCurrency } from '../../../utils/baseUtils';
import { TableRowsSkeleton } from '../../../components/skeleton/skeleton';
import ConnectStatusBadge from './connect-status-badge';
import COPY from './connect-finance-copy';

dayjs.extend(utc);

const PAGE_SIZE = 25;
const DASH = '—';

export const formatDay = (iso) => (iso ? dayjs(iso).format('MMM D, YYYY') : DASH);

// Stripe's payout arrival_date is a date-only value the API serialises as
// midnight UTC. Formatting it in the viewer's local time (formatDay) can
// print the wrong calendar day west of UTC (e.g. America/Los_Angeles reads
// a Sep 12 arrival as Sep 11), so the Arrival column reads it back in UTC.
export const formatUtcDay = (iso) => (iso ? dayjs.utc(iso).format('MMM D, YYYY') : DASH);

// Tabular figures only for the actual currency digits — the dash placeholder
// is not a number and reads fine with the surrounding text's proportional
// figures — so a column of amounts lines up down the row instead of drifting
// with each value's own width.
const money = (value) =>
  typeof value === 'number' ? (
    <span className="tabular-nums">{formatCurrency(value)}</span>
  ) : (
    DASH
  );

const words = (value) => (value ? String(value).replace(/_/g, ' ') : DASH);

const orderCell = (orderId) =>
  orderId ? (
    <Link
      to={'/dashboard/orders/' + orderId}
      title={orderId}
      className="font-medium text-blue-600 hover:underline dark:text-sky-400"
    >
      {String(orderId).slice(-8)}
    </Link>
  ) : (
    DASH
  );

const codeCell = (value) =>
  value ? <span className="font-mono text-xs">{value}</span> : DASH;

// Same "last part" treatment as the order cell: the full id is long and only
// its tail distinguishes rows at a glance, with the full value on hover.
const idTailCell = (value) =>
  value ? (
    <span className="font-mono text-xs" title={value}>
      {String(value).slice(-8)}
    </span>
  ) : (
    DASH
  );

// Client-side paging, 25 rows a page, the way PayoutTransactionsTable slices
// its own list. The page resets whenever the row set changes (period change,
// refresh) so a shorter result never leaves the table on an empty page.
const usePagedRows = (rows) => {
  const [current, setCurrent] = useState(1);
  useEffect(() => setCurrent(1), [rows]);
  const total = rows.length;
  const start = (current - 1) * PAGE_SIZE;
  const paged = rows.slice(start, start + PAGE_SIZE);
  const pagination =
    total > PAGE_SIZE
      ? { current, pageSize: PAGE_SIZE, total, showSizeChanger: false, size: 'small', onChange: setCurrent }
      : undefined;
  return { paged, pagination };
};

const Table = ({ columns, rows, empty, flush = false }) => {
  const { paged, pagination } = usePagedRows(rows);
  return (
    <TWDataTable
      columns={columns}
      data={paged}
      rowKey="id"
      pagination={pagination}
      empty={<span className="text-sm text-slate-500 dark:text-slate-400">{empty}</span>}
      // Inside a SectionCard with noBodyPadding the table has to supply the
      // card's own 20px horizontal inset itself — see the
      // `tw-table-card-inset` modifier in ui.css for why the base
      // tw-table-wrapper rules can't just be changed to do this. A flush
      // section has no card and no inset, which is this table's original
      // home: the base rules already zero the first column so it lines up
      // with the page.
      className={flush ? undefined : 'tw-table-card-inset'}
    />
  );
};

const dateColumn = { title: 'Date', dataIndex: 'createdAt', key: 'createdAt', width: 130, render: formatDay };
const orderColumn = { title: 'Order', dataIndex: 'orderId', key: 'orderId', width: 120, render: orderCell };
const statusColumn = {
  title: 'Status',
  dataIndex: 'status',
  key: 'status',
  width: 130,
  render: (status) => <ConnectStatusBadge status={status} />,
};
const moneyColumn = (title, dataIndex, width = 110) => ({
  title,
  dataIndex,
  key: dataIndex,
  width,
  align: 'right',
  render: money,
});

const commercialPaymentColumns = [
  dateColumn,
  orderColumn,
  moneyColumn('Gross', 'gross'),
  moneyColumn('Platform fee', 'platformFee'),
  moneyColumn('Card fee', 'cardFee'),
  moneyColumn('Thisway fee', 'thiswayFee'),
  moneyColumn('Net', 'restaurantNet'),
];

const restaurantPaymentColumns = [...commercialPaymentColumns, statusColumn];

// A non-US card: Stripe's surcharge raised the collected card fee above the
// agreed rate. Admin-only, like the buy-rate columns it explains.
const intlColumn = {
  title: 'Intl',
  dataIndex: 'international',
  key: 'international',
  width: 70,
  render: (flag) => (flag ? <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs font-medium text-slate-700 dark:bg-slate-700 dark:text-slate-200">Intl</span> : DASH),
};

// The admin viewer alone sees Thisway's application fee and Stripe's buy rate
// — and only once the reconciliation control below is switched on. The three
// buy-rate keys exist only on rows the API filled (adapter), so `money`
// dashes them where absent rather than printing $0.00.
const reconciliationColumns = [
  moneyColumn('Application fee', 'applicationFee', 130),
  moneyColumn('Stripe fee', 'stripeProcessingFee'),
  moneyColumn('Margin', 'cardFeeMargin'),
  moneyColumn('Delta', 'reconciliationDelta'),
  intlColumn,
];

// Status is commercial, not reconciliation — a restaurant sees it too — so it
// stays outside `reconciliationColumns` and is appended unconditionally here,
// last, in both modes.
const adminPaymentColumns = (showReconciliation) => [
  ...commercialPaymentColumns,
  ...(showReconciliation ? reconciliationColumns : []),
  statusColumn,
];

export const PaymentsTable = ({ rows, viewer, empty }) => {
  const isAdmin = viewer === 'admin';
  // Thirteen columns don't fit a laptop screen, so the five reconciliation
  // ones open behind this admin-only control instead of always being in the
  // way. Plain component state, deliberately: it's a per-admin convenience,
  // not shared state, but the dashboard has no existing idiom for persisting
  // a UI preference like this one (the app's only sessionStorage/localStorage
  // use today is the auth token) and the brief for this control is explicit
  // that one shouldn't be invented just to back it. This still "remembers"
  // the choice for as long as this table stays mounted — surviving a period
  // change or a Refresh — and resets, like any other component state, when
  // the admin navigates away from the Payments tab.
  //
  // Restaurants never see this control, and turning it on is impossible for
  // them by construction, not by hiding a button: the branch below simply
  // never renders for `viewer !== 'admin'`, and `restaurantPaymentColumns`
  // (used whenever `isAdmin` is false) has no dependency on this state at
  // all — there is no code path connecting a restaurant's render to a
  // buy-rate column.
  const [showReconciliation, setShowReconciliation] = useState(false);
  const columns = isAdmin ? adminPaymentColumns(showReconciliation) : restaurantPaymentColumns;
  return (
    <>
      {isAdmin ? (
        <div className="flex items-center justify-end gap-2 border-b border-slate-100 px-5 py-2.5 dark:border-slate-700">
          <span className="text-xs font-medium text-slate-600 dark:text-slate-300">
            {COPY.admin.reconciliationToggle.label}
          </span>
          <ToggleSwitch
            checked={showReconciliation}
            onChange={setShowReconciliation}
            ariaLabel={COPY.admin.reconciliationToggle.ariaLabel}
          />
        </div>
      ) : null}
      <Table columns={columns} rows={rows} empty={empty} />
    </>
  );
};

const refundColumns = [
  dateColumn,
  orderColumn,
  moneyColumn('Amount', 'amount'),
  statusColumn,
  { title: 'Reason', dataIndex: 'reason', key: 'reason', render: words },
  { title: 'Refund id', dataIndex: 'stripeRefundId', key: 'stripeRefundId', render: idTailCell },
];

export const RefundsTable = ({ rows, empty, flush }) => (
  <Table columns={refundColumns} rows={rows} empty={empty} flush={flush} />
);

const disputeColumns = [
  dateColumn,
  orderColumn,
  moneyColumn('Amount', 'amount'),
  statusColumn,
  { title: 'Reason', dataIndex: 'reason', key: 'reason', render: words },
];

export const DisputesTable = ({ rows, empty, flush }) => (
  <Table columns={disputeColumns} rows={rows} empty={empty} flush={flush} />
);

// A returned payout is Stripe's `failed` with the API's `returned` flag: the
// admin reads the same word the restaurant's Payouts page shows.
const payoutStatusColumn = {
  ...statusColumn,
  render: (status, row) => <ConnectStatusBadge status={row?.returned && status === 'failed' ? 'returned' : status} />,
};

const payoutColumns = [
  dateColumn,
  moneyColumn('Amount', 'amount'),
  payoutStatusColumn,
  { title: 'Arrival', dataIndex: 'arrivalDate', key: 'arrivalDate', width: 130, render: formatUtcDay },
  { title: 'Destination', dataIndex: 'destination', key: 'destination', render: codeCell },
];

export const PayoutsTable = ({ rows, empty, flush }) => (
  <Table columns={payoutColumns} rows={rows} empty={empty} flush={flush} />
);

const applicationFeeColumns = [
  dateColumn,
  moneyColumn('Amount', 'amount'),
  statusColumn,
  { title: 'Fee id', dataIndex: 'stripeApplicationFeeId', key: 'stripeApplicationFeeId', render: codeCell },
];

export const ApplicationFeesTable = ({ rows, empty, flush }) => (
  <Table columns={applicationFeeColumns} rows={rows} empty={empty} flush={flush} />
);

// One card per table. A failed read shows its own sentence and Retry in
// place of the body so the other sections stay on screen untouched.
//
// `srTitle` names the section for the Retry/Loading accessible names alone,
// without also drawing SectionCard's visible <h2>: Order Reports and
// Refunds already have their own page-level <h1> (PageHeader) right above
// this card, so a second, identical heading here would just repeat it —
// they pass `title=""` (no visible heading) but still need the subject a
// screen-reader user hears on "Retry"/"Loading" to say more than that. Falls
// back to `title` so every other caller (the tabbed admin/restaurant
// screens, which do want the visible heading) needs no change.
export const ConnectFinanceSection = ({
  title,
  description,
  error,
  loading = false,
  onRetry,
  retryLabel,
  note,
  srTitle,
  flush = false,
  children,
}) => {
  const subject = String(srTitle || title || '').toLowerCase();
  // The 20px inset belongs to the card. Without a card there is nothing to
  // line up with except the page, and the page's own gutter already does it.
  const inset = flush ? '' : 'px-5';
  return (
    <SectionCard title={title} description={description} noBodyPadding flush={flush}>
      {error ? (
        <div className={cn('flex flex-wrap items-center justify-between gap-3 py-4', inset)}>
          <p className="text-sm text-rose-600 dark:text-rose-400">{error}</p>
          <TWButton
            size="sm"
            label={retryLabel}
            onClick={onRetry}
            aria-label={subject ? retryLabel + ' ' + subject : retryLabel}
          />
        </div>
      ) : loading ? (
        <div className={inset} role="status" aria-label={subject ? 'Loading ' + subject : 'Loading'}>
          <TableRowsSkeleton count={4} rowHeight="h-9" />
        </div>
      ) : (
        <>
          {note ? (
            <p className={cn('border-b border-slate-100 py-3 text-xs text-slate-500 dark:border-slate-700 dark:text-slate-400', inset)}>
              {note}
            </p>
          ) : null}
          {children}
        </>
      )}
    </SectionCard>
  );
};
